import type { FC, ReactNode } from 'react'
import type {
    BagItem,
    BagNotice,
    BagView,
    Product
} from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react'

const STORAGE_KEY = 'aveline-bag'
const MAX_QUANTITY = 5
const NOTICE_DURATION = 4200

type BagContextValue = {
    items: BagItem[]
    count: number
    subtotal: number
    isOpen: boolean
    view: BagView
    notice: BagNotice | null
    addItem: (product: Product) => void
    reserve: (product: Product) => void
    setQuantity: (slug: string, quantity: number) => void
    removeItem: (slug: string) => void
    clear: () => void
    openBag: (view?: BagView) => void
    closeBag: () => void
    setView: (view: BagView) => void
    dismissNotice: () => void
    reconcile: (products: Product[]) => void
}

const BagContext = createContext<BagContextValue | null>(null)

const toBagItem = (product: Product, quantity: number): BagItem => ({
    slug: product.slug,
    cmsId: product.id,
    name: product.name,
    category: product.category,
    price: product.price,
    image: product.image,
    quantity
})

const isBagItem = (value: unknown): value is BagItem => {
    if (!value || typeof value !== 'object') return false

    const item = value as Record<string, unknown>

    return typeof item.slug === 'string'
        && typeof item.name === 'string'
        && typeof item.price === 'number'
        && typeof item.quantity === 'number'
}

interface BagProviderProps {
    children: ReactNode
}

export const BagProvider: FC<BagProviderProps> = ({ children }): ReactNode => {
    const [items, setItems] = useState<BagItem[]>([])
    const [isOpen, setIsOpen] = useState(false)
    const [view, setView] = useState<BagView>('bag')
    const [notice, setNotice] = useState<BagNotice | null>(null)
    const itemsRef = useRef<BagItem[]>([])

    // Every change goes through here: state, the ref used by callbacks, and the saved copy.
    const commit = useCallback((next: BagItem[]) => {
        itemsRef.current = next
        setItems(next)

        try {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next))
        }
        catch {
            // Storage can be unavailable (private browsing); the bag still works for this visit.
        }
    }, [])

    // Restore the saved bag once, after the first render, so server and client markup match.
    useEffect(() => {
        try {
            const saved = window.localStorage.getItem(STORAGE_KEY)
            if (!saved) return

            const parsed: unknown = JSON.parse(saved)
            if (!Array.isArray(parsed)) return

            const restored = parsed.filter(isBagItem).map((item) => ({
                ...item,
                cmsId: typeof item.cmsId === 'string' ? item.cmsId : null,
                category: typeof item.category === 'string' ? item.category : '',
                image: typeof item.image === 'string' ? item.image : null,
                quantity: Math.min(MAX_QUANTITY, Math.max(1, Math.floor(item.quantity)))
            }))

            itemsRef.current = restored
            setItems(restored)
        }
        catch {
            // A damaged saved bag is simply ignored.
        }
    }, [])

    useEffect(() => {
        if (!notice) return

        const timer = window.setTimeout(() => setNotice(null), NOTICE_DURATION)
        return () => window.clearTimeout(timer)
    }, [notice])

    const addItem = useCallback((product: Product) => {
        const current = itemsRef.current
        const existing = current.find((item) => item.slug === product.slug)

        const next = existing
            ? current.map((item) => item.slug === product.slug
                ? { ...item, quantity: Math.min(MAX_QUANTITY, item.quantity + 1) }
                : item)
            : [...current, toBagItem(product, 1)]

        commit(next)
        setNotice({ id: Date.now(), name: product.name, cmsId: product.id })
    }, [commit])

    const reserve = useCallback((product: Product) => {
        const current = itemsRef.current

        if (!current.some((item) => item.slug === product.slug)) {
            commit([...current, toBagItem(product, 1)])
        }

        setNotice(null)
        setView('reserve')
        setIsOpen(true)
    }, [commit])

    const setQuantity = useCallback((slug: string, quantity: number) => {
        const amount = Math.min(MAX_QUANTITY, Math.max(1, Math.floor(quantity)))
        commit(itemsRef.current.map((item) => item.slug === slug ? { ...item, quantity: amount } : item))
    }, [commit])

    const removeItem = useCallback((slug: string) => {
        commit(itemsRef.current.filter((item) => item.slug !== slug))
    }, [commit])

    const clear = useCallback(() => commit([]), [commit])

    const openBag = useCallback((nextView: BagView = 'bag') => {
        setNotice(null)
        setView(nextView)
        setIsOpen(true)
    }, [])

    const closeBag = useCallback(() => setIsOpen(false), [])

    const dismissNotice = useCallback(() => setNotice(null), [])

    // Keeps names, prices and pictures in the bag in step with the current catalog.
    const reconcile = useCallback((products: Product[]) => {
        if (products.length === 0) return

        const current = itemsRef.current
        if (current.length === 0) return

        const bySlug = new Map(products.map((product) => [product.slug, product]))
        let changed = false

        const next = current.flatMap((item) => {
            const product = bySlug.get(item.slug)

            if (!product) {
                changed = true
                return []
            }

            const updated = toBagItem(product, item.quantity)

            if (updated.name !== item.name
                || updated.price !== item.price
                || updated.image !== item.image
                || updated.category !== item.category
                || updated.cmsId !== item.cmsId) {
                changed = true
            }

            return [updated]
        })

        if (changed) commit(next)
    }, [commit])

    const value = useMemo<BagContextValue>(() => ({
        items,
        count: items.reduce((total, item) => total + item.quantity, 0),
        subtotal: items.reduce((total, item) => total + item.price * item.quantity, 0),
        isOpen,
        view,
        notice,
        addItem,
        reserve,
        setQuantity,
        removeItem,
        clear,
        openBag,
        closeBag,
        setView,
        dismissNotice,
        reconcile
    }), [items, isOpen, view, notice, addItem, reserve, setQuantity, removeItem, clear, openBag, closeBag, dismissNotice, reconcile])

    return (
        <BagContext.Provider value={value}>
            {children}
        </BagContext.Provider>
    )
}

export const useBag = (): BagContextValue => {
    const context = useContext(BagContext)

    if (!context) {
        throw new Error('useBag must be used inside BagProvider.')
    }

    return context
}
